/**
 * utils/alarmEvaluation.ts
 * Pure evaluation functions for the clock alarm system.
 *
 * Provides timezone resolution, time-part extraction, and per-rule-type
 * fire checks used by the `useAlarmEngine` hook. All functions are pure
 * (no side effects, no React dependencies) and suitable for unit testing.
 *
 * Evaluation approach:
 * - Each tick (1s), the engine resolves the current time in each rule's
 *   timezone, then checks if the rule's fire condition is met.
 * - Deterministic dedupe via trigger keys prevents double-fires without
 *   timing-window edge cases.
 * - Malformed rules (bad intervals, out-of-range values) silently return
 *   `shouldFire: false` — no throws.
 *
 * @see types/shared/AlarmRule.ts — AlarmRule discriminated union
 * @see hooks/useAlarmEngine.ts — lifecycle hook that calls these functions
 */

import type { AlarmRule } from '@/types/shared/AlarmRule';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Browser's local timezone (computed once at module scope) */
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Seconds tolerance for fire condition checks.
 * A tick may land at :00 or :01 due to setInterval drift — both should match.
 */
const SECONDS_TOLERANCE = 1;

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

/**
 * Validate an IANA timezone string.
 *
 * @param tz - Timezone identifier to validate (e.g., "America/New_York")
 * @returns `true` if the timezone is recognized by the runtime
 */
function isValidTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolve the effective timezone for an alarm rule.
 *
 * Resolution chain (first valid match wins):
 * 1. Per-rule `timezoneOverride` (if present and valid)
 * 2. `displayTimezone` from BasicSettings (if valid)
 * 3. Browser's local timezone (last-resort fallback)
 *
 * @param rule - Alarm rule with optional timezoneOverride
 * @param displayTimezone - User's display timezone from BasicSettings
 * @returns Resolved IANA timezone string
 */
export function resolveAlarmTimezone(rule: AlarmRule, displayTimezone: string): string {
  if (rule.timezoneOverride && isValidTimezone(rule.timezoneOverride)) {
    return rule.timezoneOverride;
  }
  if (displayTimezone && isValidTimezone(displayTimezone)) {
    return displayTimezone;
  }
  return BROWSER_TIMEZONE;
}

// ---------------------------------------------------------------------------
// Time-part extraction
// ---------------------------------------------------------------------------

/** Time components extracted from a Date in a specific timezone */
export interface TimeParts {
  /** Hour of day (0-23) */
  hours: number;
  /** Minute of hour (0-59) */
  minutes: number;
  /** Second of minute (0-59) */
  seconds: number;
  /** Total minutes from midnight (hours * 60 + minutes) */
  totalMinutes: number;
  /** Date in resolved timezone (YYYY-MM-DD) for day-unique trigger keys */
  dateKey: string;
}

/**
 * Extract time components from a Date in the given timezone.
 *
 * Uses `Intl.DateTimeFormat.formatToParts()` for timezone-aware extraction
 * without manual UTC offset math.
 *
 * @param date - The current Date to extract from
 * @param timezone - IANA timezone string (must be valid)
 * @returns Time parts in the target timezone
 */
export function getTimePartsInTimezone(date: Date, timezone: string): TimeParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(date);

  const year = parts.find((p) => p.type === 'year')?.value ?? '0000';
  const month = parts.find((p) => p.type === 'month')?.value ?? '00';
  const day = parts.find((p) => p.type === 'day')?.value ?? '00';
  // hour12:false can yield "24" at midnight in en-US (hourCycle h24) — normalize to 0
  const rawHours = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const hours = rawHours % 24;
  const minutes = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const seconds = Number(parts.find((p) => p.type === 'second')?.value ?? 0);

  return {
    hours,
    minutes,
    seconds,
    totalMinutes: hours * 60 + minutes,
    dateKey: `${year}-${month}-${day}`,
  };
}

// ---------------------------------------------------------------------------
// Evaluation result type
// ---------------------------------------------------------------------------

/** Result of evaluating a single alarm rule against current time */
export interface AlarmEvalResult {
  /** Whether the rule's fire condition is met and it hasn't already fired */
  shouldFire: boolean;
  /** Dedupe key — set when shouldFire is true. Stored to prevent re-firing. */
  triggerKey?: string;
}

/** Shorthand for "do not fire" result */
const NO_FIRE: AlarmEvalResult = { shouldFire: false };

// ---------------------------------------------------------------------------
// Per-type evaluators (private)
// ---------------------------------------------------------------------------

/**
 * Evaluate a candle-close-countdown rule.
 *
 * Candles close at multiples of `candleIntervalMinutes` from midnight.
 * The alert fires `alertBeforeMinutes` before each close.
 *
 * Example: candleIntervalMinutes=60, alertBeforeMinutes=1
 * → candles close at :00 each hour, alert fires at :59 of previous hour
 */
function evaluateCandleCloseCountdown(
  candleIntervalMinutes: number,
  alertBeforeMinutes: number,
  timeParts: TimeParts,
  dateKey: string,
  lastFiredKey: string | undefined,
): AlarmEvalResult {
  // Malformed-rule guards
  if (
    !Number.isFinite(candleIntervalMinutes) ||
    candleIntervalMinutes <= 0 ||
    !Number.isFinite(alertBeforeMinutes) ||
    alertBeforeMinutes < 0 ||
    alertBeforeMinutes >= candleIntervalMinutes
  ) {
    return NO_FIRE;
  }

  const { totalMinutes, seconds } = timeParts;

  // Minutes into the current candle cycle
  const minutesIntoCycle = totalMinutes % candleIntervalMinutes;
  // The alert fires when we're at (candleInterval - alertBefore) minutes into the cycle
  const targetMinuteInCycle = candleIntervalMinutes - alertBeforeMinutes;

  if (minutesIntoCycle !== targetMinuteInCycle || seconds > SECONDS_TOLERANCE) {
    return NO_FIRE;
  }

  // Trigger key = date + candle cycle number (day-unique, prevents re-fire until next candle)
  const triggerKey = `${dateKey}:${Math.floor(totalMinutes / candleIntervalMinutes)}`;

  if (lastFiredKey === triggerKey) return NO_FIRE;

  return { shouldFire: true, triggerKey };
}

/**
 * Evaluate a fixed-interval rule.
 *
 * Fires at every multiple of `intervalMinutes` from midnight.
 *
 * Example: intervalMinutes=15 → fires at :00, :15, :30, :45
 */
function evaluateFixedInterval(
  intervalMinutes: number,
  timeParts: TimeParts,
  dateKey: string,
  lastFiredKey: string | undefined,
): AlarmEvalResult {
  // Malformed-rule guard
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) {
    return NO_FIRE;
  }

  const { totalMinutes, seconds } = timeParts;

  if (totalMinutes % intervalMinutes !== 0 || seconds > SECONDS_TOLERANCE) {
    return NO_FIRE;
  }

  // Trigger key = date + totalMinutes (day-unique)
  const triggerKey = `${dateKey}:${totalMinutes}`;

  if (lastFiredKey === triggerKey) return NO_FIRE;

  return { shouldFire: true, triggerKey };
}

/**
 * Evaluate a clock-mark rule.
 *
 * Fires when the current minute matches any value in `minuteMarks`.
 *
 * Example: minuteMarks=[0, 15, 30, 45] → fires at :00, :15, :30, :45 every hour
 */
function evaluateClockMark(
  minuteMarks: number[],
  timeParts: TimeParts,
  dateKey: string,
  lastFiredKey: string | undefined,
): AlarmEvalResult {
  // Malformed-rule guard: reject if empty or any entry is invalid
  if (!Array.isArray(minuteMarks) || minuteMarks.length === 0) {
    return NO_FIRE;
  }
  for (const mark of minuteMarks) {
    if (!Number.isFinite(mark) || mark < 0 || mark > 59) {
      return NO_FIRE;
    }
  }

  const { minutes, seconds, totalMinutes } = timeParts;

  if (seconds > SECONDS_TOLERANCE) return NO_FIRE;

  // Check if current minute matches any mark (array is pre-validated)
  if (!minuteMarks.some((mark) => mark === minutes)) return NO_FIRE;

  // Trigger key = date + totalMinutes (day-unique)
  const triggerKey = `${dateKey}:${totalMinutes}`;

  if (lastFiredKey === triggerKey) return NO_FIRE;

  return { shouldFire: true, triggerKey };
}

/**
 * Evaluate a specific-time rule.
 *
 * Fires once per calendar day when the current time matches the target "HH:MM".
 * Uses a date-scoped trigger key (`${dateKey}:fired`) so it can re-fire on
 * subsequent days within the same mount lifecycle.
 *
 * Example: time="09:30" → fires at 09:30:00 (±1s) each day
 */
function evaluateSpecificTime(
  time: string,
  timeParts: TimeParts,
  dateKey: string,
  lastFiredKey: string | undefined,
): AlarmEvalResult {
  // Malformed-rule guard: validate HH:MM format
  if (typeof time !== 'string' || !/^\d{2}:\d{2}$/.test(time)) {
    return NO_FIRE;
  }

  const [hourStr, minuteStr] = time.split(':');
  const targetHour = Number(hourStr);
  const targetMinute = Number(minuteStr);

  // Validate range
  if (targetHour < 0 || targetHour > 23 || targetMinute < 0 || targetMinute > 59) {
    return NO_FIRE;
  }

  const { hours, minutes, seconds } = timeParts;

  if (hours !== targetHour || minutes !== targetMinute || seconds > SECONDS_TOLERANCE) {
    return NO_FIRE;
  }

  // Date-scoped key — fires once per calendar day in the resolved timezone
  const triggerKey = `${dateKey}:fired`;

  if (lastFiredKey === triggerKey) return NO_FIRE;

  return { shouldFire: true, triggerKey };
}

// ---------------------------------------------------------------------------
// Main evaluation dispatcher
// ---------------------------------------------------------------------------

/**
 * Evaluate whether an alarm rule should fire given the current time.
 *
 * Dispatches to the appropriate per-type evaluator based on `rule.type`.
 * Checks the trigger key against `lastFiredKey` for deduplication.
 * Returns `{ shouldFire: false }` for disabled rules, unknown types,
 * or malformed rule parameters.
 *
 * @param rule - The alarm rule to evaluate
 * @param timeParts - Current time components in the rule's resolved timezone
 * @param lastFiredKey - Previous trigger key for this rule (from dedupe map), or undefined if never fired
 * @returns Evaluation result with optional trigger key for dedupe tracking
 */
export function evaluateAlarmRule(
  rule: AlarmRule,
  timeParts: TimeParts,
  lastFiredKey: string | undefined,
): AlarmEvalResult {
  const { dateKey } = timeParts;

  switch (rule.type) {
    case 'candle-close-countdown':
      return evaluateCandleCloseCountdown(
        rule.candleIntervalMinutes,
        rule.alertBeforeMinutes,
        timeParts,
        dateKey,
        lastFiredKey,
      );
    case 'fixed-interval':
      return evaluateFixedInterval(
        rule.intervalMinutes,
        timeParts,
        dateKey,
        lastFiredKey,
      );
    case 'clock-mark':
      return evaluateClockMark(
        rule.minuteMarks,
        timeParts,
        dateKey,
        lastFiredKey,
      );
    case 'specific-time':
      return evaluateSpecificTime(
        rule.time,
        timeParts,
        dateKey,
        lastFiredKey,
      );
    default:
      return NO_FIRE;
  }
}
